import React from "react";
import { forceSimulation, forceLink, forceManyBody, forceCenter } from "d3";

const useForceSimulation = (
  nodes,
  links,
  controls,
  width,
  height,
  zoom,
  callback
) => {
  // Use useRef for mutable variables that we want to persist
  // without triggering a re-render on their change

  const requestRef = React.useRef();
  const simulationRef = React.useRef();
  const hashRef = React.useRef();
  const frameCount = React.useRef();
  const animate = React.useRef();

  // https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  React.useEffect(() => {
    animate.current = () => {
      frameCount.current++;

      let isForceLayoutTicking = simulationRef.current.alpha() > 0.05;
      if (isForceLayoutTicking) {
        simulationRef.current.tick();
      }

      callback(
        nodes,
        links,
        hashRef.current,
        frameCount.current,
        isForceLayoutTicking
      );

      requestRef.current = requestAnimationFrame(animate.current);
    };
  }, controls.concat([nodes, links, callback]));

  // This particular useEffect is ... essentially componentWillMount?
  React.useEffect(() => {
    frameCount.current = 0;

    simulationRef.current = forceSimulation(nodes)
      .force(
        "link",
        forceLink(links).id(function(d) {
          return d.id;
        })
      )
      .force("charge", forceManyBody())
      .force("center", forceCenter(width / zoom / 2, height / zoom / 2))
      .stop();

    const hash = {};
    nodes.forEach(n => {
      hash[n.id] = {
        node: n,
        neighbours: []
      };
    });

    links.forEach(l => {
      hash[l.source.id].neighbours.push({
        id: l.target.id,
        priority: l.priority
      });
      hash[l.target.id].neighbours.push({
        id: l.source.id,
        priority: l.priority
      });
    });

    hashRef.current = hash;

    requestRef.current = requestAnimationFrame(animate.current);

    return () => cancelAnimationFrame(requestRef.current);
  }, []); // Make sure the effect runs only once
};

export default useForceSimulation;
